<template>
  <div class="color-changer-tool">
    <!-- ============ ENGLISH ============ -->
    <div class="compressor-wrapper" v-if="currentLanguage == 'en'">
      
      <div class="main-layout">
        
        <!-- LEFT PANEL: Preview -->
        <div class="left-panel">
          <div
            class="drop-zone"
            :class="{ dragging: isDragging }"
            v-if="!isImageLoaded"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
            @click="$refs.fileInput.click()"
          >
            <div class="drop-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M512 256c0 .9 0 1.8 0 2.7c-.4 36.5-33.6 61.3-70.1 61.3H344c-26.5 0-48 21.5-48 48c0 3.4 .4 6.7 1 9.9c2.1 10.2 6.5 20 10.8 29.9c6.1 13.8 12.1 27.5 12.1 42c0 31.8-21.6 60.7-53.4 62c-3.5 .1-7 .2-10.6 .2C114.6 512 0 397.4 0 256S114.6 0 256 0S512 114.6 512 256zM128 288a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm0-96a32 32 0 1 0 0-64 32 32 0 1 0 0 64zM288 96a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm96 96a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/></svg>
            </div>
            <p class="drop-text">Drag & Drop an image here</p>
            <p class="drop-hint">or</p>
            <button class="choose-btn" @click.stop="$refs.fileInput.click()">Choose Image</button>
            <p class="drop-formats">Supports JPG, PNG, WebP</p>
          </div>

          <div class="preview-card" v-else>
            <div class="preview-label" style="display: flex; justify-content: space-between;">
              <span v-if="isProcessing">Analyzing Colors...</span>
              <span v-else>Live Preview</span>
              <button class="reset-icon-btn" @click.stop="reset">✕ Clear</button>
            </div>
            <div class="canvas-container">
               <canvas ref="mainCanvasEn" class="editor-canvas"></canvas>
            </div>
          </div>
        </div>

        <!-- RIGHT PANEL: Settings (Always Visible) -->
        <div class="right-panel">
          <div class="settings-card">
            <div class="card-title">🎨 Dominant Colors</div>
            
            <p v-if="!isImageLoaded" class="empty-text" style="padding: 20px 0; border: 1.5px dashed #e5e7eb; border-radius: 12px;">Upload an image to extract colors here.</p>
            <p v-else-if="dominantColors.length === 0" class="empty-text">Processing colors...</p>
            
            <div class="colors-list" v-else>
              <div v-for="(colorObj, index) in dominantColors" :key="index" class="color-item">
                <div class="color-preview" :style="{ backgroundColor: colorObj.originalHex }"></div>
                <div class="color-info">
                  <label>Original #{{ index + 1 }}</label>
                  <span>{{ colorObj.originalHex }}</span>
                </div>
                <div class="color-info">
                  <label>Replace With</label>
                  <input type="color" class="color-picker" v-model="colorObj.replacementHex" @input="scheduleApplyChanges">
                </div>
              </div>
            </div>

            <div class="divider"></div>

            <div class="input-group">
               <label class="field-label" style="display: flex; justify-content: space-between;">
                 <span>Tolerance (Accuracy)</span>
                 <span style="color: #3a7afe; font-weight: bold;">{{ tolerance }}</span>
               </label>
               <input type="range" class="range-slider" min="5" max="100" v-model.number="tolerance" @input="scheduleApplyChanges" :disabled="!isImageLoaded">
               <p class="help-text">* Higher values replace larger color areas.</p>
            </div>

            <div class="action-area mt-4">
               <button class="download-btn w-100" @click="downloadImage" :disabled="!isImageLoaded || isProcessing">
                 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                 Download Edited Image
               </button>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- ============ ARABIC ============ -->
    <div class="compressor-wrapper" v-else>
      
      <div class="main-layout">
        
        <!-- LEFT PANEL (Appears on right in RTL): Preview -->
        <div class="left-panel">
          <div
            class="drop-zone"
            :class="{ dragging: isDragging }"
            v-if="!isImageLoaded"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
            @click="$refs.fileInput.click()"
          >
            <div class="drop-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M512 256c0 .9 0 1.8 0 2.7c-.4 36.5-33.6 61.3-70.1 61.3H344c-26.5 0-48 21.5-48 48c0 3.4 .4 6.7 1 9.9c2.1 10.2 6.5 20 10.8 29.9c6.1 13.8 12.1 27.5 12.1 42c0 31.8-21.6 60.7-53.4 62c-3.5 .1-7 .2-10.6 .2C114.6 512 0 397.4 0 256S114.6 0 256 0S512 114.6 512 256zM128 288a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm0-96a32 32 0 1 0 0-64 32 32 0 1 0 0 64zM288 96a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm96 96a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/></svg>
            </div>
            <p class="drop-text">اسحب وأفلت الصورة هنا</p>
            <p class="drop-hint">أو</p>
            <button class="choose-btn" @click.stop="$refs.fileInput.click()">اختر صورة</button>
            <p class="drop-formats">يدعم JPG و PNG و WebP</p>
          </div>

          <div class="preview-card" v-else>
            <div class="preview-label" style="display: flex; justify-content: space-between;">
              <span v-if="isProcessing">جاري تحليل الألوان...</span>
              <span v-else>المعاينة الحية</span>
              <button class="reset-icon-btn" @click.stop="reset">✕ مسح الصورة</button>
            </div>
            <div class="canvas-container">
               <canvas ref="mainCanvasAr" class="editor-canvas"></canvas>
            </div>
          </div>
        </div>

        <!-- RIGHT PANEL (Appears on left in RTL): Settings -->
        <div class="right-panel">
          <div class="settings-card">
            <div class="card-title">🎨 التحكم بالألوان السائدة</div>
            
            <p v-if="!isImageLoaded" class="empty-text" style="padding: 20px 0; border: 1.5px dashed #e5e7eb; border-radius: 12px;">قم برفع صورة لاستخراج وتعديل ألوانها هنا.</p>
            <p v-else-if="dominantColors.length === 0" class="empty-text">جاري استخراج الألوان...</p>
            
            <div class="colors-list" v-else>
              <div v-for="(colorObj, index) in dominantColors" :key="index" class="color-item">
                <div class="color-preview" :style="{ backgroundColor: colorObj.originalHex }"></div>
                <div class="color-info">
                  <label>اللون الأصلي #{{ index + 1 }}</label>
                  <span>{{ colorObj.originalHex }}</span>
                </div>
                <div class="color-info">
                  <label>اللون الجديد</label>
                  <input type="color" class="color-picker" v-model="colorObj.replacementHex" @input="scheduleApplyChanges">
                </div>
              </div>
            </div>

            <div class="divider"></div>

            <div class="input-group">
               <label class="field-label" style="display: flex; justify-content: space-between;">
                 <span>دقة التبديل (Tolerance)</span>
                 <span style="color: #3a7afe; font-weight: bold;">{{ tolerance }}</span>
               </label>
               <input type="range" class="range-slider" min="5" max="100" v-model.number="tolerance" @input="scheduleApplyChanges" :disabled="!isImageLoaded">
               <p class="help-text">* زيادة القيمة تؤدي لتبديل مساحات لونية أكبر.</p>
            </div>

            <div class="action-area mt-4">
               <button class="download-btn w-100" @click="downloadImage" :disabled="!isImageLoaded || isProcessing">
                 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                 تحميل الصورة المعدلة
               </button>
            </div>
          </div>
        </div>

      </div>
    </div>

    <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleFileUpload" />
  </div>
</template>

<script>
import { notifyToolSuccess } from '@/utils/promoPopup';

export default {
  name: 'ColorChangerCom',
  data() {
    return {
      currentLanguage: this.$store.state.currentLanguage,
      isDragging: false,
      isImageLoaded: false,
      isProcessing: false,
      
      originalImageData: null,
      dominantColors: [],
      tolerance: 20,
      
      canvasCtx: null,
      activeCanvas: null,
      
      applyTimeout: null // For debouncing input
    };
  },
  watch: {
    '$store.state.currentLanguage': function(newLang) {
      this.currentLanguage = newLang;
      this.$nextTick(() => {
        if(this.isImageLoaded) {
           this.bindCanvas();
           this.applyChanges();
        }
      });
    }
  },
  methods: {
    handleDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) this.processFile(file);
    },
    handleFileUpload(e) {
      const file = e.target.files[0];
      if (file) this.processFile(file);
      this.$refs.fileInput.value = '';
    },
    processFile(file) {
      this.isProcessing = true;
      this.isImageLoaded = true;
      this.dominantColors = [];
      this.tolerance = 20;
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          this.bindCanvas();
          
          const MAX_WIDTH = 1200;
          let width = img.width;
          let height = img.height;
          
          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }

          this.activeCanvas.width = width;
          this.activeCanvas.height = height;
          
          this.canvasCtx.drawImage(img, 0, 0, width, height);
          this.originalImageData = this.canvasCtx.getImageData(0, 0, width, height);
          
          setTimeout(() => {
            this.extractDominantColors();
          }, 100);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    },
    
    bindCanvas() {
      this.activeCanvas = this.currentLanguage === 'en' ? this.$refs.mainCanvasEn : this.$refs.mainCanvasAr;
      this.canvasCtx = this.activeCanvas.getContext('2d', { willReadFrequently: true });
    },

    extractDominantColors() {
      const data = this.originalImageData.data;
      const colorCounts = {};
      
      for (let i = 0; i < data.length; i += 40) {
        if(data[i+3] < 128) continue;
        const r = data[i];
        const g = data[i+1];
        const b = data[i+2];
        
        const key = `${Math.round(r/10)*10},${Math.round(g/10)*10},${Math.round(b/10)*10}`;
        colorCounts[key] = (colorCounts[key] || 0) + 1;
      }

      const sortedColors = Object.entries(colorCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(item => item[0].split(',').map(Number));

      this.dominantColors = sortedColors.map(rgb => {
        const hex = this.rgbToHex(rgb[0], rgb[1], rgb[2]);
        return {
          originalRgb: rgb,
          originalHex: hex,
          replacementHex: hex
        };
      });

      this.isProcessing = false;
    },

    // Debounce method to prevent freezing during fast drag
    scheduleApplyChanges() {
      if (this.applyTimeout) {
        clearTimeout(this.applyTimeout);
      }
      this.applyTimeout = setTimeout(() => {
        this.applyChanges();
      }, 50); // 50ms delay is enough to keep it smooth
    },

    applyChanges() {
      if (!this.originalImageData || !this.canvasCtx) return;
      
      const newImageData = new ImageData(
        new Uint8ClampedArray(this.originalImageData.data),
        this.originalImageData.width,
        this.originalImageData.height
      );
      const data = newImageData.data;
      
      const replacements = this.dominantColors.map(c => this.hexToRgb(c.replacementHex));
      
      // OPTIMIZATION: Use squared tolerance to avoid heavy Math.sqrt() in the loop
      const tolSq = this.tolerance * this.tolerance;

      for (let i = 0; i < data.length; i += 4) {
        if(data[i+3] === 0) continue;

        const r = data[i];
        const g = data[i+1];
        const b = data[i+2];

        for (let idx = 0; idx < this.dominantColors.length; idx++) {
          const domRGB = this.dominantColors[idx].originalRgb;
          
          const dr = r - domRGB[0];
          const dg = g - domRGB[1];
          const db = b - domRGB[2];

          // OPTIMIZATION: Avoid Math.pow(), just multiply directly.
          const distanceSq = (dr * dr) + (dg * dg) + (db * db);

          if (distanceSq < tolSq) {
            data[i] = replacements[idx].r;
            data[i+1] = replacements[idx].g;
            data[i+2] = replacements[idx].b;
            break; 
          }
        }
      }
      this.canvasCtx.putImageData(newImageData, 0, 0);
    },

    rgbToHex(r, g, b) {
      return "#" + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
    },

    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 0, g: 0, b: 0 };
    },

    downloadImage() {
      if (!this.activeCanvas) return;
      const link = document.createElement('a');
      link.download = 'SplendApp_ColorChanger.png';
      link.href = this.activeCanvas.toDataURL('image/png');
      link.click();
      notifyToolSuccess('color-changer');
    },

    reset() {
      this.isImageLoaded = false;
      this.originalImageData = null;
      this.dominantColors = [];
      if(this.canvasCtx) {
          this.canvasCtx.clearRect(0,0, this.activeCanvas.width, this.activeCanvas.height);
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400&family=DM+Mono:wght@400;500&display=swap');

* { box-sizing: border-box; }

.compressor-wrapper {
  font-family: 'DM Sans', 'Cairo', sans-serif;
  max-width: 1000px; 
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
  background: #F6F9FB;
  min-height: fit-content;
}
[dir="rtl"] .compressor-wrapper { direction: rtl; text-align: right; }

.main-layout {
  display: flex;
  gap: 25px;
  align-items: flex-start;
  justify-content: center;
}

.left-panel { flex: 1; min-width: 0; width: 100%; transition: 0.3s; }
.right-panel {
  width: 340px; flex-shrink: 0; position: sticky; top: 25px;
}

@media (max-width: 850px) {
  .main-layout { flex-direction: column; }
  .right-panel { width: 100%; position: static; }
}

.drop-zone {
  background: white; border: 2.5px dashed #c7c9f5; border-radius: 20px;
  padding: 4rem 2rem; text-align: center; cursor: pointer;
  transition: border-color .2s, background .2s;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.drop-zone.dragging { border-color: #3a7afe; background: #eef0ff; }
.drop-icon svg { width: 52px; height: 52px; fill: #3a7afe; margin-bottom: 1rem; opacity: .85; }
.drop-text { font-size: 1.1rem; font-weight: 600; color: #24365c; margin: 0 0 .35rem; }
.drop-hint { color: #716c80; font-size: .85rem; margin: 0 0 .8rem; }
.drop-formats { color: #716c80; font-size: .8rem; margin: .8rem 0 0; }

.choose-btn {
  background: #3a7afe; color: white; border: none; border-radius: 10px;
  padding: .6rem 1.6rem; font-family: inherit; font-size: .95rem;
  font-weight: 600; cursor: pointer; transition: background .2s, transform .1s;
}
.choose-btn:hover { background: #0053fe; transform: translateY(-1px); }

.preview-card {
  background: white; border-radius: 16px; overflow: hidden;
  border: 1.5px solid #e5e7eb; display: flex; flex-direction: column;
  box-shadow: 0 4px 15px rgba(0,0,0,0.02);
}
.preview-label {
  font-size: .85rem; font-weight: 700; color: #24365c; padding: .8rem 1rem; 
  border-bottom: 1px solid #e5e7eb; background: #f9fafb; align-items: center;
}
.canvas-container {
  padding: 15px; background: repeating-conic-gradient(#f3f4f6 0% 25%, transparent 0% 50%) 50% / 20px 20px;
  display: flex; justify-content: center; align-items: center; min-height: 300px;
}
.editor-canvas {
  max-width: 100%; max-height: 500px; object-fit: contain; box-shadow: 0 4px 10px rgba(0,0,0,0.1); border-radius: 8px;
}

.settings-card { 
  background: white; border-radius: 16px; padding: 20px; border: 1.5px solid #e5e7eb; box-shadow: 0 4px 15px rgba(0,0,0,0.02);
}
.card-title { font-weight: 700; font-size: 1.1rem; color: #24365c; margin-bottom: 1.2rem; display: flex; align-items: center; gap: 8px; }
.empty-text { font-size: 0.85rem; color: #716c80; text-align: center; margin-bottom: 10px; }

.colors-list { display: flex; flex-direction: column; gap: 12px; }
.color-item {
  display: flex; align-items: center; gap: 12px; background: #f9fafb; padding: 12px; border-radius: 12px; border: 1px solid #e5e7eb;
}
.color-preview {
  width: 45px; height: 45px; border-radius: 10px; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.1); flex-shrink: 0;
}
.color-info { flex: 1; display: flex; flex-direction: column; }
.color-info label { font-size: 0.75rem; font-weight: 700; color: #716c80; margin-bottom: 4px; }
.color-info span { font-size: 0.8rem; font-family: 'DM Mono', monospace; font-weight: bold; color: #24365c; }
.color-picker {
  width: 100%; height: 35px; border: 1.5px solid #e5e7eb; border-radius: 8px; padding: 1px; cursor: pointer; background: white;
}

.divider { height: 1px; background: #e5e7eb; margin: 20px 0; }
.input-group { margin-bottom: 15px; }
.field-label { display: block; font-size: .85rem; font-weight: 600; color: #716c80; margin-bottom: .8rem; }
.help-text { font-size: 0.75rem; color: #9ca3af; margin-top: 6px; }

.range-slider {
  -webkit-appearance: none; width: 100%; height: 6px; background: #e5e7eb; border-radius: 5px; outline: none;
}
.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none; width: 18px; height: 18px; border-radius: 50%; background: #3a7afe; cursor: pointer; transition: 0.2s;
}
.range-slider::-webkit-slider-thumb:hover { transform: scale(1.2); }
.range-slider:disabled::-webkit-slider-thumb { background: #9ca3af; cursor: not-allowed; }

.download-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: .6rem; background: #3a7afe; color: white; 
  border: none; border-radius: 12px; padding: .85rem; font-family: inherit; font-size: 1rem; font-weight: 700; cursor: pointer;
  transition: background .2s, transform .1s; box-shadow: 0 4px 16px rgba(58, 122, 254, .3);
}
.download-btn.w-100 { width: 100%; }
.download-btn svg { width: 18px; height: 18px; }
.download-btn:hover:not(:disabled) { background: #0053fe; transform: translateY(-2px); }
.download-btn:disabled { background: #9ca3af; cursor: not-allowed; box-shadow: none; transform: none; }

.reset-icon-btn { background: none; border: none; color: #ef4444; font-size: 0.75rem; font-weight: bold; cursor: pointer; padding: 0; }
.reset-icon-btn:hover { text-decoration: underline; color: #dc2626; }
</style>
